import {
  Button,
  Card,
  CardContent,
  Input,
  CardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@altimateai/lego";
import { Stack, LoadingButton } from "@altimateai/lego";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { postSlackWebhook } from "@/helpers/apis";
import { GET_NOTIFICATION } from "../notifications/useNotifications";
import {
  AddNotificationChannelProps,
  AddNotificationChannelFormData,
  addNotificationChannelSchema,
  NotificationChannelType,
} from "../../types";
import { CardTitle } from "@altimateai/lego";
import { SettingsTracking } from "@/lib/tracking/settings";

const AddNotificationChannel = ({ toggleAddNotificationChannel }: AddNotificationChannelProps) => {
  const queryClient = useQueryClient();

  const form = useForm<AddNotificationChannelFormData>({
    resolver: zodResolver(addNotificationChannelSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      type: NotificationChannelType.SLACK,
      webhook_url: "",
    },
  });

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: postSlackWebhook,
    onSuccess: () => {
      SettingsTracking.notificationChannelAdded(NotificationChannelType.SLACK);
      queryClient.invalidateQueries({ queryKey: [GET_NOTIFICATION] });
      toggleAddNotificationChannel();
    },
  });

  const onSubmit = (data: AddNotificationChannelFormData) => {
    mutate({
      webhook_url: data.webhook_url,
      name: data.name,
    });
  };

  return (
    <div>
      <Card className="al-w-[50vw]">
        <CardHeader>
          <CardTitle>Add Notification Channel</CardTitle>
        </CardHeader>
        <CardContent className="al-p-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="al-mb-4">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} type="text" placeholder="Enter Slack channel name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="al-mb-4">
                    <FormLabel>Type</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder={NotificationChannelType.SLACK}
                        disabled
                        value={NotificationChannelType.SLACK}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="webhook_url"
                render={({ field }) => (
                  <FormItem className="al-mb-4">
                    <FormLabel>Webhook URL</FormLabel>
                    <FormControl>
                      <Input {...field} type="url" placeholder="Paste the webhook URL here" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Stack direction="row" gap="md" justify="end" className="al-mt-8">
                <Button type="button" variant="outline" onClick={toggleAddNotificationChannel}>
                  Cancel
                </Button>
                <LoadingButton type="submit" disabled={!form.formState.isValid} loading={isLoading}>
                  Save
                </LoadingButton>
              </Stack>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddNotificationChannel;
